import React, {useCallback, useContext, useEffect, useState} from 'react';

import {useDateValidation} from 'src/hooks/useDateValidation';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import Button from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {Group} from '@pb/kit-component-group';
import SlideBox, {SlideBoxButton} from '@pb/kit-component-slide-box';
import {LocaleContext} from '@pb/kit-context-locale';
import {stringToNumber} from '@pb/kit-formatters';

import {
    apiV2ContractItem,
    apiV2ContractItemDetails,
    apiV2ContractItemDetailsExpanded,
    apiV2ContractItemExpanded,
    apiV2ContractItemExpandedDetails,
    apiV2ContractItemExpired,
    apiV2ContractItemHandle,
} from '../../contracts/components/contract-item.module.styl';
import {getEndDate} from '../../contracts/utils';
import {getData, getOverageCost} from '../utils';

import {ChangeCreditForm} from './change-credit-form';
import {ChangelogTable} from './changelog-table';
import {ConfirmationModal} from './confirmation-modal';
import {CONFIRM} from './constants';
import {ContractTable} from './contract-table';
import {PricingTable} from './pricing-table';

import type {ContractsItemDto} from '../../contracts/typedef';
import type {OnUpdateContractInfo, OnUpdateCredits} from '../typedef';
import type {ConfirmTypes} from './constants';
import type {OnChangeComment, OnChangeCredits} from './typedef';
import type {ContractDetails} from 'src/openapi/admin/models/ContractDetails';
import type {IntegrationContractDetailed} from 'src/openapi/admin/models/IntegrationContractDetailed';


type Props = {
    data: ContractsItemDto,
    allContractNumbers: Array<string>,
    isPastContract: boolean | null,
    lastCreatedNumber: string | null,
    onUpdateContractInfo: OnUpdateContractInfo,
    onUpdateCredits: OnUpdateCredits,
    creditsDataAvailable: boolean,
    disabled?: boolean,
};

const COLLAPSED_FORM_DATA = {
    formExpanded: false,
    confirmType: null,
    creditsAmount: '',
    comment: '',
};

export const ContractItem = (props: Props) => {
    const {
        data,
        isPastContract,
        creditsDataAvailable,
        allContractNumbers,
        lastCreatedNumber,
        onUpdateContractInfo,
        onUpdateCredits,
        disabled,
    } = props;

    const {locale} = useContext(LocaleContext);

    const [expanded, setExpanded] = useState<boolean>(lastCreatedNumber === data.contractInfo.contractNumber);
    const [startDate, setStartDate] = useState<string>(data.contractInfo.startDate);
    const [endDate, setEndDate] = useState<string>(getEndDate(data));
    const [contractNumber, setContractNumber] = useState(data.contractInfo.contractNumber);
    const [contractNumberIsUnique, setContractNumberIsUnique] = useState(true);
    const [formExpanded, setFormExpanded] = useState(false);
    const [creditsAmount, setCreditsAmount] = useState('');
    const [comment, setComment] = useState('');
    const [confirmType, setConfirmType] = useState<ConfirmTypes | null>(null);

    useEffect(() => {
        setCreditsAmount('');
        setComment('');
    }, [data]);


    useEffectWithDeps((prev, current) => {
        if (prev.lastCreatedNumber !== current.lastCreatedNumber) {
            setExpanded(lastCreatedNumber === data.contractInfo.contractNumber);
        }
    }, {lastCreatedNumber, data});

    const onChangeDates = (date: string, name?: string) => {
        if (name === 'startDate') {
            setStartDate(`${date}` || '');
        }
        if (name === 'endDate') {
            setEndDate(`${date}` || '');
        }
    };

    const {hasInvalidRange} = useDateValidation(startDate, endDate, date => onChangeDates(date, 'endDate'));

    const onChangeContractNumber = useCallback((newNumber: string) => {
        const isUnique = !allContractNumbers.includes(newNumber);

        setContractNumberIsUnique(isUnique);
        setContractNumber(newNumber);
    }, [allContractNumbers]);

    const onRestoreDates = useCallback(() => {
        setStartDate(data.contractInfo.startDate);
        setEndDate(getEndDate(data));
        setContractNumber(data.contractInfo.contractNumber);
    }, [data]);

    const onSave = useCallback(() => {
        const contractId = data.contractInfo.contractId || -1;
        const pricingModel = data.contractInfo.pricingModel;

        onUpdateContractInfo({
            contractNumber,
            pricingModel,
            startDate,
            endDate,
            contractId,
        });
    }, [data, contractNumber, startDate, endDate, onUpdateContractInfo]);

    const onChangeCredits: OnChangeCredits = value => {
        setCreditsAmount(value);
    };

    const onChangeComment: OnChangeComment = value => {
        setComment(value);
    };

    const onRestoreForm = () => {
        setCreditsAmount('');
        setComment('');
    };

    const onUpdateCreditsFunc = useCallback(() => {
        const contractId = data.contractInfo.contractId || -1;
        onUpdateCredits({
            credits: stringToNumber(creditsAmount, locale),
            comment,
            contractId,
        });
    }, [data, creditsAmount, comment, locale, onUpdateCredits]);

    const isFormUnsaved = useCallback(() => {
        return creditsAmount || comment;
    }, [creditsAmount, comment]);

    const isDateUnsaved = useCallback(() => {
        const currentEndDate = getEndDate(data);

        return startDate !== data.contractInfo.startDate || endDate !== currentEndDate;
    }, [data, startDate, endDate]);

    const onToggleDetails = useCallback(() => {
        if (isDateUnsaved() || isFormUnsaved()) {
            setConfirmType(CONFIRM.DATES);
        } else {
            setFormExpanded(false);
            setExpanded(state => !state);
        }
    }, [isDateUnsaved, isFormUnsaved]);

    const onToggleForm = useCallback(() => {
        if (isFormUnsaved()) {
            setConfirmType(CONFIRM.CREDITS);
        } else {
            setFormExpanded(state => !state);
        }
    }, [isFormUnsaved]);

    const onCloseConfirmation = useCallback(() => {
        setConfirmType(null);
    }, []);

    const getCollapsedContractData = useCallback(() => {
        const newEndDate = getEndDate(data);
        const newStartDate = data.contractInfo.startDate;
        return {
            ...COLLAPSED_FORM_DATA,
            expanded: false,
            startDate: newStartDate,
            endDate: newEndDate,
        };
    }, [data]);

    const onConfirm = useCallback(() => {
        if (confirmType === CONFIRM.CREDITS) {
            return COLLAPSED_FORM_DATA;
        } else {
            return getCollapsedContractData();
        }
    }, [confirmType, getCollapsedContractData]);

    const {contractInfo, contractChanges, creditBalanceInfo} = data;

    const getContractData = (contractsItem: ContractDetails | IntegrationContractDetailed) => {
        const newEndDate = getEndDate(contractsItem);
        const overageCost = getOverageCost(contractsItem);

        return {
            endDate: newEndDate,
            overageCost,
            isSubscription: contractInfo.pricingModel !== 'PAY_PER_CALL',
        };
    };

    const {overageCost, endDate: currentEndDate, isSubscription} = getContractData(data);

    const costPerCreditValue = 'costPerCredit' in contractInfo
        ? contractInfo.costPerCredit ?? undefined
        : undefined;
    const pricingCostPerCredit = costPerCreditValue ?? null;
    const {remainingCredits} = creditBalanceInfo;

    const isChanged = contractInfo.startDate !== startDate || currentEndDate !== endDate
        || contractInfo.contractNumber !== contractNumber;
    const canSave = isChanged && startDate && (!isSubscription || isSubscription && endDate) && !hasInvalidRange;

    const canEditContract = !(contractInfo.pricingModel === 'INTEGRATION' && isPastContract) && !disabled;
    const canEditCredits = creditsDataAvailable && !disabled;

    const itemClasses = `${apiV2ContractItem} ${expanded ? apiV2ContractItemExpanded : ''} ${
        !expanded && isPastContract ? apiV2ContractItemExpired : ''
    }`;

    const detailsClasses = `${apiV2ContractItemDetails} ${expanded ? apiV2ContractItemDetailsExpanded : ''}`;

    return (
        <div className={itemClasses}>
            <div className={apiV2ContractItemHandle}>
                <SlideBoxButton onClick={onToggleDetails}>
                    {slideBoxClick => (
                        <CheckBox
                            name="default"
                            onCheckBoxChange={slideBoxClick}
                            checked={expanded}
                            custom={true}
                            usePropsOnly={true}
                        />
                    )}
                </SlideBoxButton>
            </div>

            <div className={detailsClasses}>
                <ContractTable
                    {...{
                        expanded,
                        data: getData(data),
                        startDate,
                        endDate,
                        contractNumber,
                        creditsDataAvailable,
                        showWarning: !contractNumberIsUnique && isChanged,
                        remainingCredits,
                        onChangeDates,
                        onChangeContractNumber,
                        disabled: !canEditContract,
                    }}
                />

                <SlideBox {...{shown: expanded}}>
                    <div className={apiV2ContractItemExpandedDetails}>
                        {!confirmType ? (
                            <ConfirmationModal
                                {...{
                                    confirmType,
                                    onClose: onCloseConfirmation,
                                    onConfirm,
                                }}
                            />
                        ) : null}
                        <Group direction="v" gap="l" alignItems="start">
                            {creditsDataAvailable ? (
                                <PricingTable
                                    {...{
                                        costPerCredit: pricingCostPerCredit,
                                        overageCost,
                                        creditBalanceInfo,
                                    }}
                                />
                            ) : null}
                            <Group direction="v" gap="xl" alignItems="start">
                                <ChangelogTable
                                    {...{
                                        creditsDataAvailable,
                                        data: contractChanges,
                                    }}
                                />
                                {canEditCredits ? (
                                    <ChangeCreditForm
                                        {...{
                                            costPerCredit: costPerCreditValue,
                                            formExpanded,
                                            creditsAmount,
                                            remainingCredits,
                                            comment,
                                            onToggleForm,
                                            onChangeCredits,
                                            onChangeComment,
                                            onRestoreForm,
                                            onUpdateCredits: onUpdateCreditsFunc,
                                            disabled,
                                        }}
                                    />
                                ) : null}
                            </Group>
                            {canEditContract ? (
                                <Group>
                                    <Button
                                        variant="Type.PRIMARY"
                                        disabled={!canSave || !contractNumberIsUnique}
                                        onClick={onSave}>
                                        Save
                                    </Button>
                                    <Button variant="Type.SECONDARY" disabled={!isChanged} onClick={onRestoreDates}>
                                        Cancel
                                    </Button>
                                </Group>
                            ) : null}
                        </Group>
                    </div>
                </SlideBox>
            </div>
        </div>
    );
};
